import { Button, Card, ControlGroup, FormGroup } from "@blueprintjs/core";
import type { ExtraFieldTypeArray } from "@package/ui-gql/index-data-access";
import { get, set } from "lodash";
import { useMemo } from "react";
import { ServiceDataFormField } from "../ServiceDataFormField";
import type { ServiceDataFormFieldBaseProps } from "./ServiceDataFormFieldEmpty";

type ServiceDataFormFieldArrayProps = ServiceDataFormFieldBaseProps & {
  field: ExtraFieldTypeArray;
};

function ServiceDataFormFieldArrayContent({
  data,
  field,
  onSourceChanged,
}: ServiceDataFormFieldArrayProps) {
  const entries = useMemo(() => get(data, field.source), [data, field.source]);

  const moveEntry = (indexEntry: number, offset: number) => {
    onSourceChanged((draft) => {
      const draftEntries = get(draft, field.source);
      const target = indexEntry + offset;

      if (
        Array.isArray(draftEntries) &&
        target >= 0 &&
        target < draftEntries.length
      ) {
        const [item] = draftEntries.splice(indexEntry, 1);
        draftEntries.splice(target, 0, item);
      }
    });
  };

  return (
    <FormGroup>
      {Array.isArray(entries) &&
        entries.map((entry, indexEntry) => (
          <FormGroup
            key={indexEntry}
            label={
              field.showTitle
                ? `${field.label} ${indexEntry + 1}`
                : String(indexEntry + 1)
            }
          >
            <Card>
              <ControlGroup fill>
                <ControlGroup fill vertical>
                  {field.extraFields.map((child, indexField) => {
                    return (
                      <ServiceDataFormField
                        key={indexField}
                        field={child}
                        data={entry}
                        onSourceChanged={(callback) => {
                          onSourceChanged((draft) => {
                            const draftEntries = get(draft, field.source);

                            if (
                              Array.isArray(draftEntries) &&
                              indexEntry < draftEntries.length
                            ) {
                              callback(draftEntries[indexEntry]);
                            }
                          });
                        }}
                      />
                    );
                  })}
                </ControlGroup>
                <div className="flex flex-none flex-col">
                  <Button
                    minimal
                    icon="arrow-up"
                    disabled={indexEntry === 0}
                    onClick={() => moveEntry(indexEntry, -1)}
                  />
                  <Button
                    minimal
                    icon="arrow-down"
                    disabled={indexEntry === entries.length - 1}
                    onClick={() => moveEntry(indexEntry, 1)}
                  />
                  <Button
                    minimal
                    intent="danger"
                    icon="remove"
                    onClick={() =>
                      onSourceChanged((draft) => {
                        const draftEntries = get(draft, field.source);

                        if (
                          Array.isArray(draftEntries) &&
                          draftEntries.length >= indexEntry
                        ) {
                          draftEntries.splice(indexEntry, 1);
                        }
                      })
                    }
                  />
                </div>
              </ControlGroup>
            </Card>
          </FormGroup>
        ))}
      <Button
        minimal
        icon="add"
        intent="primary"
        text={`Add ${field.label}`}
        onClick={() =>
          onSourceChanged((draft) => {
            const draftEntries = get(draft, field.source) ?? [];

            set(draft, field.source, [...draftEntries, {}]);
          })
        }
      />
    </FormGroup>
  );
}

export function ServiceDataFormFieldArray({
  field,
  onSourceChanged,
  data,
}: ServiceDataFormFieldBaseProps) {
  if (field.__typename === "ExtraFieldTypeArray") {
    return (
      <ServiceDataFormFieldArrayContent
        field={field}
        data={data}
        onSourceChanged={onSourceChanged}
      />
    );
  }

  return null;
}
